import { useEffect, useRef, useState, type FormEvent, type RefObject } from 'react';
import { Navigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  ApiError,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ErrorText,
  Input,
  Label,
  PageTitle,
} from '@dataroom/shared';
import {
  downloadKit,
  EVENT_LABELS,
  formatDay,
  formatWhen,
  keysCall,
  KIT_CODE_HINT,
  KIT_CODE_PLACEHOLDER,
  KITS_QUERY_KEY,
  PURPOSE_LABELS,
  PURPOSES,
  readKitFile,
  recoveryReasonLabel,
  RETIRABLE,
  RETIRE_POLL_MS,
  tierLabel,
  type CreatedKit,
  type KeyPurpose,
  type KeysStatus,
  type KeyVersionView,
} from '@/lib/keys';

/**
 * The platform owner's KEYS console (ADR-0069 slice B): recovery status, the
 * company recovery kit (create → store → Verify), rotation and retirement.
 *
 * The kit and its code exist only in this page's memory: the code is shown
 * ONCE, the file is downloaded from an in-memory Blob, and both are dropped
 * when the owner confirms they are stored in the company password manager.
 * While the code is on screen the status is not re-read (a failed re-read must
 * never replace the code with an error) and leaving the page asks first.
 * This page never loads the analytics tag (main.tsx skips it here; the Keys
 * link is a full page load and the router reloads any other way in, App.tsx)
 * and its path carries a strict CSP (public/_headers). The API decides
 * everything; a 404 (not a platform owner) sends the person home.
 */

const codeInput = (value: string) => value.replace(/\D/g, '').slice(0, 6);
const VERIFY_INTERVAL_MS = 90 * 24 * 60 * 60 * 1000;

type ManagedStatus = Extract<KeysStatus, { managed: true }>;

export function PlatformKeysPage() {
  // A tab that already loaded the analytics tag on another page reloads, so
  // this page runs without it (main.tsx never loads it on /platform/keys).
  const tagLoaded = typeof window !== 'undefined' && typeof window.gtag === 'function';
  useEffect(() => {
    if (tagLoaded) window.location.reload();
  }, [tagLoaded]);

  // True while a new kit's code is on screen: the status is not re-read.
  const [holding, setHolding] = useState(false);
  const status = useQuery({
    queryKey: KITS_QUERY_KEY,
    queryFn: () => keysCall(() => api.get<KeysStatus>('/platform/keys')),
    retry: false,
    enabled: !tagLoaded && !holding,
    refetchInterval: (query) => {
      const data = query.state.data;
      return data?.managed && data.retiring.length > 0 ? RETIRE_POLL_MS : false;
    },
  });

  if (tagLoaded) return <p className="px-6 py-6 text-sm text-muted-foreground">Loading…</p>;
  if (!status.data && status.error instanceof ApiError && status.error.status === 401) {
    return <Navigate to="/login" replace />;
  }
  if (!status.data && status.error instanceof ApiError && status.error.status === 404) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="mx-auto w-full max-w-container space-y-6 px-4 py-6 md:px-6">
      <PageTitle>Encryption keys</PageTitle>
      {status.data ? (
        <>
          {/* A failed re-read shows above; what was on the page stays. */}
          <ErrorText error={status.error} />
          {!status.data.managed ? (
            <Card>
              <CardContent className="py-4 text-sm">
                Key management isn’t switched on for this site yet. There is nothing to do here.
              </CardContent>
            </Card>
          ) : (
            <Managed data={status.data} onHolding={setHolding} />
          )}
        </>
      ) : status.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="space-y-2">
          <ErrorText error={status.error} />
          <Button variant="outline" onClick={() => void status.refetch()}>
            Check again
          </Button>
        </div>
      )}
    </div>
  );
}

function Managed({ data, onHolding }: { data: ManagedStatus; onHolding: (on: boolean) => void }) {
  const r = data.recovery;
  const verifyRef = useRef<HTMLHeadingElement>(null);
  const pendingDates = r.pendingCreatedAt ?? [];
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Recovery</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <p>
            <strong data-testid="recovery-state">
              {r.armed ? 'Recovery is ready' : 'Recovery is not ready'}
            </strong>
            {r.armed
              ? ' — every key is backed up in a Verified recovery kit.'
              : ' — the keys can’t be recovered yet if the server’s master keys are ever lost or replaced.'}
          </p>
          {r.reasons.length > 0 ? (
            <ul className="list-disc pl-5" aria-label="Why recovery is not ready">
              {r.reasons.map((reason) => (
                <li key={reason}>{recoveryReasonLabel(reason, r)}</li>
              ))}
            </ul>
          ) : null}
          <dl className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-[14rem_1fr]">
            <dt className="text-muted-foreground">Current kit</dt>
            <dd>{r.currentId ? `Kit created ${formatDay(r.currentCreatedAt)}` : 'None yet'}</dd>
            <dt className="text-muted-foreground">Last Verified</dt>
            <dd>{formatWhen(r.verifiedAt)}</dd>
            <dt className="text-muted-foreground">Next Verify due</dt>
            <dd>
              {formatWhen(r.verifyDueAt)}
              {r.verifyOverdue ? (
                <span className="ml-2 font-medium text-destructive">
                  overdue — Verify the kit now
                </span>
              ) : null}
            </dd>
            <dt className="text-muted-foreground">Platform owners</dt>
            <dd>{data.platformOwners}</dd>
            <dt className="text-muted-foreground">Site</dt>
            <dd>{tierLabel(data.tier)}. Kits only work on the site that made them.</dd>
          </dl>
          {r.pendingIds.length > 0 ? (
            <p>
              A newer kit
              {pendingDates.length > 0
                ? ` (created ${pendingDates.map((d) => formatDay(d)).join(', ')})`
                : ''}{' '}
              is waiting to be Verified.
            </p>
          ) : null}
        </CardContent>
      </Card>
      <CreateKit
        onHolding={onHolding}
        onDone={() => {
          // Next step: Verify the stored copy.
          verifyRef.current?.focus();
        }}
      />
      <VerifyKit headingRef={verifyRef} currentId={r.currentId} />
      <Versions data={data} />
      <Events data={data} />
    </>
  );
}

/** Create → the code shown once + the file downloaded → confirm stored → dropped. */
function CreateKit({
  onHolding,
  onDone,
}: {
  onHolding: (on: boolean) => void;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const [totpCode, setTotpCode] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [stored, setStored] = useState(false);
  const [copied, setCopied] = useState<'yes' | 'failed' | null>(null);
  const send = useMutation({
    mutationFn: () => keysCall(() => api.post('/platform/keys/kit/email-code', {})),
  });
  const create = useMutation({
    mutationFn: () =>
      keysCall(() => api.post<CreatedKit>('/platform/keys/kit', { totpCode, emailCode })),
    onSuccess: (created) => {
      setTotpCode('');
      setEmailCode('');
      setStored(false);
      setCopied(null);
      onHolding(true);
      downloadKit(created);
    },
  });
  const created = create.data;

  // Leaving (or reloading) the page while the code is on screen asks first.
  useEffect(() => {
    if (!created) return;
    const guard = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, [created]);

  if (created) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Your new recovery kit</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p>
            Your browser should have saved{' '}
            <span className="break-all font-mono">{created.fileName}</span> to your Downloads
            folder. If you don’t see it, click Download the kit file again.
          </p>
          <p>
            This is the recovery code. It is shown <strong>once</strong> and ClosingVault does not
            keep it:
          </p>
          <p
            className="select-all rounded-md border bg-muted px-3 py-2 font-mono text-base tracking-wider"
            data-testid="recovery-code"
          >
            {created.recoveryCode}
          </p>
          <p className="text-muted-foreground">
            If you lose this code before you Verify, nothing breaks. The old kit keeps working. Just
            create another kit.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => downloadKit(created)}>
              Download the kit file again
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                const clip = navigator.clipboard;
                if (!clip) {
                  setCopied('failed');
                  return;
                }
                clip.writeText(created.recoveryCode).then(
                  () => setCopied('yes'),
                  () => setCopied('failed'),
                );
              }}
            >
              {copied === 'yes' ? 'Code copied' : 'Copy code'}
            </Button>
          </div>
          {copied === 'failed' ? (
            <p role="alert" className="text-destructive">
              The code couldn’t be copied. Select it above and copy it by hand.
            </p>
          ) : null}
          <p>
            Put the file and the code in the company password manager, where at least two people can
            reach them. Then Verify the stored copy below: until it is Verified the kit does not
            count.
          </p>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={stored} onChange={(e) => setStored(e.target.checked)} />
            I saved the kit file and the code in the company password manager, and checked the code
            matches.
          </label>
          <Button
            disabled={!stored}
            onClick={() => {
              create.reset();
              onHolding(false);
              void qc.invalidateQueries({ queryKey: KITS_QUERY_KEY });
              onDone();
            }}
          >
            Done — clear them from this page
          </Button>
        </CardContent>
      </Card>
    );
  }

  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle>Create a recovery kit</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p>
          The kit is the company’s only way back to every customer’s data if the server’s master
          keys are ever lost or replaced. Creating one needs a code from your authenticator app and
          a code we email you. A new kit replaces the old one once it is Verified.
        </p>
        <Button variant="outline" disabled={send.isPending} onClick={() => send.mutate()}>
          {send.isSuccess ? 'Email me another code' : 'Email me a code'}
        </Button>
        {send.isSuccess ? <p>Code sent. It expires in 15 minutes.</p> : null}
        <ErrorText error={send.error} />
        <form className="grid max-w-md gap-3" onSubmit={submit} aria-label="Create a recovery kit">
          <div className="grid gap-1">
            <Label htmlFor="kit-email-code">Emailed code</Label>
            <Input
              id="kit-email-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={emailCode}
              onChange={(e) => setEmailCode(codeInput(e.target.value))}
            />
          </div>
          <div className="grid gap-1">
            <Label htmlFor="kit-totp-code">Authenticator code</Label>
            <Input
              id="kit-totp-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={totpCode}
              onChange={(e) => setTotpCode(codeInput(e.target.value))}
            />
          </div>
          <Button
            type="submit"
            disabled={create.isPending || emailCode.length !== 6 || totpCode.length !== 6}
          >
            {create.isPending ? 'Creating…' : 'Create the kit'}
          </Button>
          <ErrorText error={create.error} />
        </form>
      </CardContent>
    </Card>
  );
}

/** Verify: the stored kit file and its code must open every key. */
function VerifyKit({
  headingRef,
  currentId,
}: {
  headingRef: RefObject<HTMLHeadingElement>;
  currentId: string | null;
}) {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [code, setCode] = useState('');
  const [dueAt, setDueAt] = useState<string | null>(null);
  const [replaced, setReplaced] = useState(false);
  const verify = useMutation({
    mutationFn: async () => {
      const kit = await readKitFile(file);
      return keysCall(() =>
        api.post<{ verified: boolean; recoveryKeyId: string; versions: number }>(
          '/platform/keys/kit/verify',
          { kit, recoveryCode: code },
        ),
      );
    },
    onSuccess: (result) => {
      setCode('');
      setDueAt(new Date(Date.now() + VERIFY_INTERVAL_MS).toISOString());
      // An old kit stopped working only if there was one and this is a different kit
      // (judged against the status shown before the re-read below).
      setReplaced(currentId !== null && result.recoveryKeyId !== currentId);
      void qc.invalidateQueries({ queryKey: KITS_QUERY_KEY });
    },
  });
  return (
    <Card>
      <CardHeader>
        <h2
          ref={headingRef}
          tabIndex={-1}
          className="text-lg font-semibold leading-none tracking-tight outline-none"
        >
          Verify the recovery kit
        </h2>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p>
          Use the copy from the company password manager. ClosingVault opens every key with it and
          changes nothing else. Verify again every 90 days.
        </p>
        <form
          className="grid max-w-md gap-3"
          aria-label="Verify the recovery kit"
          onSubmit={(e) => {
            e.preventDefault();
            verify.mutate();
          }}
        >
          <div className="grid gap-1">
            <Label htmlFor="verify-kit-file">Kit file</Label>
            <input
              id="verify-kit-file"
              type="file"
              accept="application/json,.json"
              // w-full: the native picker's own width would widen the page on a phone.
              className="block w-full text-sm file:mr-3 file:rounded-md file:border file:border-input file:bg-background file:px-3 file:py-1.5 file:text-sm file:font-medium"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </div>
          <div className="grid gap-1">
            <Label htmlFor="verify-kit-code">Recovery code</Label>
            <Input
              id="verify-kit-code"
              autoComplete="off"
              spellCheck={false}
              placeholder={KIT_CODE_PLACEHOLDER}
              aria-describedby="verify-kit-code-hint"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            <p id="verify-kit-code-hint" className="text-xs text-muted-foreground">
              {KIT_CODE_HINT}
            </p>
          </div>
          <Button type="submit" disabled={verify.isPending || code.trim() === ''}>
            {verify.isPending ? 'Verifying…' : 'Verify'}
          </Button>
          {verify.isSuccess ? (
            <p role="status" data-testid="verify-success">
              {replaced
                ? 'Verified. This kit is now the company’s recovery kit, and the old one no longer works.'
                : 'Verified. This kit is now the company’s recovery kit.'}{' '}
              Next check due {formatDay(dueAt)}.
            </p>
          ) : null}
          <ErrorText error={verify.error} />
        </form>
      </CardContent>
    </Card>
  );
}

/** Each kind of key, its versions, and Make a new key / Retire. */
function Versions({ data }: { data: ManagedStatus }) {
  const armed = data.recovery.armed;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Keys</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6 text-sm">
        <p>
          A new key takes new data a few minutes after it is made, and old data stays readable.
          Retiring moves everything off an old key in the background; nothing is ever deleted.
        </p>
        {PURPOSES.map((purpose) => (
          <PurposeKeys
            key={purpose}
            purpose={purpose}
            versions={data.versions.filter((v) => v.purpose === purpose)}
            armed={armed}
            retiring={data.retiring.filter((r) => r.purpose === purpose).map((r) => r.version)}
          />
        ))}
      </CardContent>
    </Card>
  );
}

type Act = { kind: 'rotate' } | { kind: 'retire'; version: number };

/**
 * Why a version that a newer one could replace has no Retire button (null when
 * it has one, or none is expected — the newest version, a retired one).
 */
function noRetireReason(
  purpose: KeyPurpose,
  v: KeyVersionView,
  sorted: KeyVersionView[],
): string | null {
  if (v.status !== 'active' || !sorted.some((o) => o.version > v.version)) return null;
  if (!RETIRABLE.has(purpose)) return 'Never retired: links already emailed must keep working.';
  const settling = sorted.find((o) => o.version > v.version && !o.writing && o.settlesAt);
  if (settling && !sorted.some((o) => o.version > v.version && o.writing)) {
    return `Can be retired once v${settling.version} takes new data (${formatWhen(settling.settlesAt)}).`;
  }
  return null;
}

function PurposeKeys({
  purpose,
  versions,
  armed,
  retiring,
}: {
  purpose: KeyPurpose;
  versions: KeyVersionView[];
  armed: boolean;
  retiring: number[];
}) {
  const qc = useQueryClient();
  const label = PURPOSE_LABELS[purpose];
  const [act, setAct] = useState<Act | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const run = useMutation({
    mutationFn: (a: Act) =>
      keysCall(() =>
        a.kind === 'retire'
          ? api.post('/platform/keys/retire', { purpose, version: a.version, totpCode })
          : api.post('/platform/keys/rotate', { purpose, totpCode }),
      ),
    onSuccess: (_data, a) => {
      setAct(null);
      setTotpCode('');
      setDone(
        a.kind === 'rotate'
          ? `Done. A new ${label} key was made. New data starts using it in a few minutes.`
          : `Retiring version ${a.version} has started. This page updates by itself.`,
      );
      void qc.invalidateQueries({ queryKey: KITS_QUERY_KEY });
    },
  });
  const open = (a: Act) => {
    setDone(null);
    run.reset();
    setTotpCode('');
    setAct(a);
  };
  const retireBusy = retiring.length > 0;
  const rotateBlocked = !armed
    ? 'Making a new key needs recovery to be ready: a new key would have no backup.'
    : retireBusy
      ? 'An older key is being retired; make a new key once that finishes.'
      : null;
  const sorted = [...versions].sort((a, b) => b.version - a.version);
  return (
    <section aria-label={label} className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-semibold">{label}</h3>
        <Button
          size="sm"
          variant="outline"
          disabled={rotateBlocked !== null}
          title={rotateBlocked ?? undefined}
          onClick={() => open({ kind: 'rotate' })}
        >
          Rotate
        </Button>
      </div>
      {rotateBlocked ? <p className="text-muted-foreground">{rotateBlocked}</p> : null}
      <table className="w-full text-left">
        <thead className="text-muted-foreground">
          <tr>
            <th className="py-1 font-normal">Version</th>
            <th className="py-1 font-normal">State</th>
            <th className="py-1" />
          </tr>
        </thead>
        <tbody>
          {sorted.map((v) => {
            const isRetiring = retiring.includes(v.version);
            const canRetire =
              RETIRABLE.has(purpose) &&
              v.status === 'active' &&
              !v.writing &&
              !retireBusy &&
              sorted.some((o) => o.version > v.version && o.writing);
            const why = canRetire || isRetiring ? null : noRetireReason(purpose, v, sorted);
            return (
              <tr key={v.version} className="border-t">
                <td className="py-1">v{v.version}</td>
                <td className="py-1">
                  {isRetiring
                    ? 'Retiring… this page updates by itself. You can leave it; the work carries on.'
                    : v.status === 'retired'
                      ? 'Retired (kept, nothing stored under it)'
                      : v.writing
                        ? 'In use for new data'
                        : v.settlesAt
                          ? `Takes new data from ${formatWhen(v.settlesAt)}`
                          : 'Readable (older)'}
                </td>
                <td className="py-1 text-right">
                  {canRetire ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => open({ kind: 'retire', version: v.version })}
                    >
                      Retire
                    </Button>
                  ) : why ? (
                    <span className="text-muted-foreground">{why}</span>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {done ? (
        <p role="status" className="font-medium">
          {done}
        </p>
      ) : null}
      {act ? (
        <form
          className="space-y-2 rounded-md border p-3"
          aria-label={act.kind === 'rotate' ? 'Confirm making a new key' : 'Confirm retirement'}
          onSubmit={(e) => {
            e.preventDefault();
            run.mutate(act);
          }}
        >
          <p data-testid="confirm-text">
            {act.kind === 'rotate'
              ? `This makes a new ${label} key. New data starts using it in a few minutes, and ` +
                'old data stays readable. This can’t be undone. The new key is added to the ' +
                'current recovery kit by itself; nothing needs downloading again.'
              : `This moves all ${label} data off version ${act.version} onto the newest key, in ` +
                `the background. When it finishes, version ${act.version} is marked retired and ` +
                'can’t be used again.'}
          </p>
          <div className="flex flex-wrap items-end gap-2">
            <div className="grid gap-1">
              <Label htmlFor={`keys-totp-${purpose}`}>Authenticator code</Label>
              <Input
                id={`keys-totp-${purpose}`}
                inputMode="numeric"
                autoComplete="one-time-code"
                // The person just asked for this; the code is the next thing to type.
                autoFocus
                value={totpCode}
                onChange={(e) => setTotpCode(codeInput(e.target.value))}
              />
            </div>
            <Button type="submit" disabled={run.isPending || totpCode.length !== 6}>
              {act.kind === 'rotate' ? 'Make a new key' : `Retire version ${act.version}`}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setAct(null)}>
              Cancel
            </Button>
          </div>
          <ErrorText error={run.error} />
        </form>
      ) : null}
    </section>
  );
}

function Events({ data }: { data: ManagedStatus }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Recent key events</CardTitle>
      </CardHeader>
      <CardContent className="text-sm">
        {data.events.length === 0 ? (
          <p className="text-muted-foreground">None yet.</p>
        ) : (
          <ul className="space-y-1" aria-label="Recent key events">
            {data.events.map((e) => (
              <li key={e.id} className="break-words">
                {formatWhen(e.at)} — {EVENT_LABELS[e.event] ?? e.event}
                {e.outcome !== 'ok' ? ` (${e.outcome})` : ''}
                {e.purpose
                  ? ` · ${PURPOSE_LABELS[e.purpose]}${e.version ? ` v${e.version}` : ''}`
                  : ''}
                {e.actorEmail ? ` · ${e.actorEmail}` : ''}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
